import type { CreateEpicKind } from '../../../../shared/types/epics';

/**
 * UX-09: the copy shown for each epic kind — the option-card title and
 * description on /epics/new, and the GitHub labels each kind actually
 * posts. Kept in one place so the card copy and the "What gets posted
 * to GitHub" preview (built by `describeIssue` below) can never drift
 * apart. See feature-specs/webportal-ux/UX-09-new-epic.md for the source
 * copy table.
 *
 *   epic       → Feature              → labels: epic, agent-ready
 *   bugfix     → Bug fix              → labels: agent:bug
 *   cross-repo → Spans multiple repos → labels: epic-cross-repo (issue on main repo)
 */
export const EPIC_KIND_META: Record<
  CreateEpicKind,
  { cardTitle: string; cardDescription: string; labels: string[] }
> = {
  epic: {
    cardTitle: 'Feature',
    cardDescription: 'Planned into stories, built and reviewed',
    labels: ['epic', 'agent-ready'],
  },
  bugfix: {
    cardTitle: 'Bug fix',
    cardDescription: 'One story, no planning step',
    labels: ['agent:bug'],
  },
  'cross-repo': {
    cardTitle: 'Spans multiple repos',
    cardDescription: 'One plan across several repos',
    labels: ['epic-cross-repo'],
  },
};

/** Card order on the /epics/new kind picker. */
export const EPIC_KIND_ORDER: CreateEpicKind[] = ['epic', 'bugfix', 'cross-repo'];

/**
 * Builds the one-line summary shown in the "What gets posted to GitHub"
 * preview: which repo the issue lands on, which labels it carries, and —
 * for cross-repo — which repos are in scope. Pure so it's trivial to unit
 * test without mounting the form (see __tests__/lib/describeIssue.test.ts).
 */
export function describeIssue(
  kind: CreateEpicKind,
  anchorRepo: string,
  involvedRepos: string[] = [],
): string {
  const repo = anchorRepo || '(repo not yet selected)';
  const labels = EPIC_KIND_META[kind].labels.join(', ');
  const base = `Issue on ${repo} · labels: ${labels}`;
  if (kind !== 'cross-repo') return base;
  const scope = involvedRepos.length > 0 ? involvedRepos.join(', ') : repo;
  return `${base} · repos in scope: ${scope}`;
}
